import { useMemo } from 'react';
import { useGlobalStore } from '@/store/global-store';
import { useStoreInitialization } from '@/store/hooks/useStoreInitialization';

/**
 * Returns a single, app-level loading boolean by combining:
 *
 * 1) Store initialization loading state (`useStoreInitialization().isLoading`)
 * 2) Global store loading state (`useGlobalStore().isLoading`)
 * 3) An optional caller-provided loading override (`additionalLoading`)
 *
 * Why this hook exists:
 * - Multiple parts of the app may have independent loading states.
 * - Components often need one simple `isLoading` value for rendering logic.
 * - This hook centralizes that merge logic so it is consistent and reusable.
 *
 * Behavior details:
 * - If `additionalLoading` is `true`, this hook immediately returns `true`.
 *   This acts as a local "force loading" condition for the caller.
 * - If `additionalLoading` is `false`, loading is derived from app-level state:
 *   `isLoadingInit || isLoadingStore`.
 *
 * Memoization notes:
 * - `useMemo` avoids recomputing the derived boolean unless one of the
 *   relevant inputs changes.
 * - The computation is small, but memoizing keeps intent explicit and aligns
 *   with common hook composition patterns in this codebase.
 *
 * @param additionalLoading Optional local loading flag supplied by a component.
 * Defaults to `false`.
 * @returns `true` when loading should be shown; otherwise `false`.
 */
export function useAppLoading(additionalLoading: boolean = false): boolean {
  // Loading state from store initialization flow (API bootstrapping, hydration, etc.).
  const { isLoading: isLoadingInit } = useStoreInitialization();

  // Loading state from the global store itself.
  const { isLoading: isLoadingStore } = useGlobalStore();

  // Derive the final loading state once from all relevant sources.
  const isLoading = useMemo(() => {
    // Caller-provided loading should take precedence when it is explicitly true.
    // This lets feature-level loading keep the UI in a loading state even if
    // the app-level store has already finished loading.
    if (additionalLoading) return additionalLoading;

    // Standard app-level behavior: loading is true when either initialization
    // or global store loading is active.
    return isLoadingInit || isLoadingStore;
  }, [isLoadingInit, isLoadingStore, additionalLoading]);

  // Return a single boolean that consumers can use directly in render logic.
  return isLoading;
}
